import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, Edit, Star, FileText, ChevronDown, ChevronUp, CheckCircle2, Building2, Globe } from 'lucide-react';

interface ReferenceArticle {
  title: string;
  url: string;
  excerpt?: string;
  publishedDate?: string;
}

interface Journalist {
  id: string;
  site_id?: string;
  sector_id?: number;
  sector_name?: string;
  sector_names?: string[];
  is_generic?: boolean;
  is_organization_exclusive?: boolean;
  organization_name?: string;
  name: string;
  title: string;
  bio?: string;
  photo_url?: string;
  style: 'professional' | 'sporty' | 'business' | 'conversational';
  voice_gender: 'male' | 'female' | 'neutral';
  reference_articles?: ReferenceArticle[];
  sample_questions?: string[];
  is_active: boolean;
  total_interviews: number;
  average_rating?: number;
  created_at: string;
}

interface JournalistCardProps {
  journalist: Journalist;
  onEdit?: (journalist: Journalist) => void;
  onSelect?: (journalist: Journalist) => void;
  showEditButton?: boolean;
  selectable?: boolean;
  isSelected?: boolean;
}

const styleColors = {
  professional: 'bg-blue-100 text-blue-800',
  sporty: 'bg-green-100 text-green-800',
  business: 'bg-purple-100 text-purple-800',
  conversational: 'bg-orange-100 text-orange-800'
};

const styleLabels = {
  professional: 'Professional',
  sporty: 'Sporty',
  business: 'Business',
  conversational: 'Conversational'
};

export function JournalistCard({
  journalist,
  onEdit,
  onSelect,
  showEditButton = false,
  selectable = false,
  isSelected = false
}: JournalistCardProps) {
  const [expanded, setExpanded] = useState(false);

  const handleSelect = () => {
    if (selectable && onSelect) {
      onSelect(journalist);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(journalist);
    }
  };

  // Generate initials from name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Card
      className={`overflow-hidden transition-all ${
        selectable ? 'cursor-pointer hover:shadow-lg hover:border-primary' : ''
      } ${!journalist.is_active ? 'opacity-60' : ''} ${
        isSelected ? 'border-4 border-green-500 shadow-lg ring-4 ring-green-100' : ''
      }`}
      onClick={handleSelect}
    >
      <CardHeader className="p-0">
        {/* Photo */}
        <div className={`relative h-64 bg-gradient-to-br overflow-hidden flex items-center justify-center ${
          isSelected ? 'from-green-100 to-emerald-100' : 'from-blue-100 to-indigo-100'
        }`}>
          <Avatar className="w-40 h-40">
            {journalist.photo_url && (
              <AvatarImage
                src={journalist.photo_url}
                alt={journalist.name}
                className="object-cover"
              />
            )}
            <AvatarFallback className="text-4xl font-bold bg-primary text-primary-foreground">
              {getInitials(journalist.name)}
            </AvatarFallback>
          </Avatar>

          {/* Selected checkmark overlay */}
          {isSelected && (
            <div className="absolute top-3 left-3 bg-green-500 rounded-full p-2">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
          )}

          {/* Style badge overlay */}
          <div className="absolute top-3 right-3 flex flex-col gap-1 items-end">
            <Badge className={styleColors[journalist.style]}>
              {styleLabels[journalist.style]}
            </Badge>
            {/* Organization badge */}
            {journalist.is_organization_exclusive ? (
              <Badge className="bg-purple-100 text-purple-800">
                <Building2 className="w-3 h-3 mr-1" />
                {journalist.organization_name || 'Organization'}
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-white/80">
                <Globe className="w-3 h-3 mr-1" />
                System
              </Badge>
            )}
          </div>

          {/* Active status */}
          {!journalist.is_active && (
            <div className="absolute bottom-3 left-3">
              <Badge variant="secondary">Inactive</Badge>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-4">
        {/* Name and Title */}
        <div className="mb-3">
          <h3 className="font-semibold text-lg">{journalist.name}</h3>
          <p className="text-sm text-muted-foreground">{journalist.title}</p>
          {/* Show sector names or indicate generic */}
          {journalist.is_generic ? (
            <p className="text-xs text-muted-foreground mt-1">All sectors</p>
          ) : journalist.sector_names && journalist.sector_names.length > 0 ? (
            <p className="text-xs text-muted-foreground mt-1">
              {journalist.sector_names.join(', ')}
            </p>
          ) : journalist.sector_name && (
            <p className="text-xs text-muted-foreground mt-1">
              {journalist.sector_name}
            </p>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mb-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <FileText className="w-4 h-4" />
            <span>{journalist.total_interviews || 0} interviews</span>
          </div>
          {journalist.average_rating && (
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span>{journalist.average_rating.toFixed(1)}</span>
            </div>
          )}
        </div>

        {/* Short bio preview */}
        {journalist.bio && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {journalist.bio}
          </p>
        )}

        {/* Expand/Collapse button */}
        <Button
          variant="ghost"
          size="sm"
          className="w-full mb-2"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? (
            <>
              <ChevronUp className="w-4 h-4 mr-2" />
              Show Less
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4 mr-2" />
              Learn More
            </>
          )}
        </Button>

        {/* Expanded content */}
        {expanded && (
          <div className="space-y-4">
            <Separator />

            {/* Full bio */}
            {journalist.bio && (
              <div>
                <h4 className="font-semibold text-sm mb-2">About</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {journalist.bio}
                </p>
              </div>
            )}

            {/* Reference articles */}
            {journalist.reference_articles && journalist.reference_articles.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2">Reference Articles</h4>
                <div className="space-y-2">
                  {journalist.reference_articles.map((article, idx) => (
                    <div key={idx} className="text-sm">
                      <a
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {article.title}
                      </a>
                      {article.excerpt && (
                        <p className="text-muted-foreground text-xs mt-1 line-clamp-2">
                          {article.excerpt}
                        </p>
                      )}
                      {article.publishedDate && (
                        <p className="text-muted-foreground text-xs">
                          {new Date(article.publishedDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sample questions */}
            {journalist.sample_questions && journalist.sample_questions.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2">Sample Questions</h4>
                <ul className="list-disc list-inside space-y-1">
                  {journalist.sample_questions.slice(0, 3).map((question, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground">
                      {question}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2 mt-3">
          {selectable && (
            <Button
              className={`flex-1 ${isSelected ? 'bg-green-600 hover:bg-green-700' : ''}`}
              onClick={handleSelect}
            >
              {isSelected ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Selected
                </>
              ) : (
                'Select Journalist'
              )}
            </Button>
          )}
          {showEditButton && (
            <Button
              variant="outline"
              onClick={handleEdit}
              className={selectable ? '' : 'flex-1'}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
